import * as React from "react";
import { cn } from "../../../lib/utils";

export interface TextAreaProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  "onChange"
> {
  /** Visual variant of the textarea */
  variant?: "default" | "unstyled";
  /** Placeholder text shown when empty */
  placeholder?: string;
  /** Error state */
  error?: boolean;
  /** Error message shown below the textarea */
  errorMessage?: string;
  /** Minimum height in pixels (default: 48) */
  minHeight?: number;
  /** Maximum height in pixels before scrolling (default: 180) */
  maxHeight?: number;
  /** Whether to auto-expand as user types */
  autoExpand?: boolean;
  /** Container className */
  containerClassName?: string;
  /** Callback when value changes */
  onChange?: (value: string) => void;
}

/**
 * TextArea - A multi-line text input component
 *
 * Features:
 * - Auto-expanding height as user types (optional)
 * - Min/max height constraints with scrolling
 * - Error state with message
 * - Focus/hover states matching Capsule design system
 * - Unstyled variant for custom styling needs
 *
 * Based on Figma design: Text_areaNew component
 *
 * @example
 * ```tsx
 * <TextArea
 *   placeholder="Enter description..."
 *   value={value}
 *   onChange={setValue}
 *   autoExpand
 * />
 * ```
 *
 * @example
 * ```tsx
 * // With error state
 * <TextArea
 *   placeholder="Required field"
 *   error
 *   errorMessage="This field is required"
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Unstyled variant for custom styling
 * <TextArea
 *   variant="unstyled"
 *   className="custom-textarea-class"
 * />
 * ```
 */
const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      variant = "default",
      placeholder,
      error = false,
      errorMessage,
      minHeight = 48,
      maxHeight = 180,
      autoExpand = true,
      containerClassName,
      className,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      disabled,
      ...props
    },
    ref,
  ) => {
    const internalRef = React.useRef<HTMLTextAreaElement>(null);
    const textareaRef =
      (ref as React.RefObject<HTMLTextAreaElement>) || internalRef;
    const [isFocused, setIsFocused] = React.useState(false);

    // Check if textarea has content
    const hasContent = Boolean(value && String(value).trim() !== "");

    // Auto-expand textarea height based on content
    React.useEffect(() => {
      if (autoExpand && textareaRef.current) {
        const textarea = textareaRef.current;
        // Reset to min height to get accurate scrollHeight
        textarea.style.height = `${minHeight}px`;
        // Calculate new height clamped between min and max
        const newHeight = Math.min(
          Math.max(textarea.scrollHeight, minHeight),
          maxHeight,
        );
        textarea.style.height = `${newHeight}px`;
      }
    }, [value, autoExpand, minHeight, maxHeight, textareaRef]);

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e.target.value);
    };

    // Unstyled variant - just the textarea without wrapper styling
    if (variant === "unstyled") {
      return (
        <div className={cn("flex w-full flex-col", containerClassName)}>
          <textarea
            ref={textareaRef}
            value={value}
            defaultValue={defaultValue}
            disabled={disabled}
            placeholder={placeholder}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            className={cn(
              "w-full resize-none bg-transparent outline-none",
              "text-sm leading-5 text-text-primary",
              "placeholder:text-text-tertiary",
              disabled && "cursor-not-allowed opacity-50",
              className,
            )}
            style={{
              minHeight: `${minHeight}px`,
              maxHeight: `${maxHeight}px`,
            }}
            {...props}
          />
          {/* Error message */}
          {error && errorMessage && (
            <p className="mt-1 px-4 text-xs leading-4 text-text-critical">
              {errorMessage}
            </p>
          )}
        </div>
      );
    }

    // Default variant - with border and wrapper styling
    return (
      <div className={cn("flex w-full flex-col", containerClassName)}>
        {/* Textarea wrapper */}
        <div
          className={cn(
            // Base styles
            "relative flex w-full flex-col overflow-hidden rounded-lg",
            // Border styles - using solid to override CSS resets
            "border border-solid",
            // Height constraints
            !autoExpand && `min-h-[${minHeight}px] max-h-[${maxHeight}px]`,
            // State-based styles
            error
              ? "border-stroke-critical bg-bg-critical"
              : isFocused || hasContent
                ? "border-stroke-primary bg-bg-primary"
                : "border-stroke-secondary bg-bg-primary hover:border-stroke-primary",
            // Disabled state
            disabled && "cursor-not-allowed bg-bg-disabled opacity-50",
          )}
        >
          <textarea
            ref={textareaRef}
            value={value}
            defaultValue={defaultValue}
            disabled={disabled}
            placeholder={placeholder}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            className={cn(
              // Base styles
              "w-full resize-none bg-transparent outline-none",
              // Padding - matching Figma specs
              "px-3 py-3",
              // Text styling
              "text-sm leading-5 text-text-primary",
              "placeholder:text-text-tertiary",
              // Scrollbar styling
              "overflow-y-auto",
              "[&::-webkit-scrollbar]:w-[6px]",
              "[&::-webkit-scrollbar-track]:bg-transparent",
              "[&::-webkit-scrollbar-thumb]:rounded-full",
              "[&::-webkit-scrollbar-thumb]:bg-icon-disabled",
              // Disabled state
              disabled && "cursor-not-allowed",
              className,
            )}
            style={{
              minHeight: `${minHeight}px`,
              maxHeight: `${maxHeight}px`,
            }}
            {...props}
          />
        </div>

        {/* Error message */}
        {error && errorMessage && (
          <p className="mt-1 px-4 text-xs leading-4 text-text-critical">
            {errorMessage}
          </p>
        )}
      </div>
    );
  },
);

TextArea.displayName = "TextArea";

export { TextArea };
